import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import parse, { DOMNode } from 'html-react-parser';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import useAccreditationStandardById from '../../../_data_sources/AccreditationStandards/useStandard';
import { translate as t } from 'hooks/useTranslations';
import { useStandardsContext } from '../context';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { neutral } from '_lex/_foundations/phxColors';
import noStandardSelected from '../../images/no_standard_selected.svg';
import StandardsList from '../List';
import EditorSheet from '_lex/_atoms/EditorSheet';
import WrittenDirectivesSummary from './WrittenDirectivesSummary';
import { Jurisdiction } from 'types/jurisdiction';
import { AppContext } from 'views/App/context';
import ActionMenu, { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import usePortfolioExportFullPolicies from '../../../_data_sources/PortfolioExports/usePortfolioExportFullPolicies';
import usePortfolioExportTaggedDirectives from '../../../_data_sources/PortfolioExports/usePortfolioExportTaggedDirectives';
import { enqueueSnackbar } from 'notistack';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import useEnabledVersionsInfo from '../../../_hooks/useEnabledVersionsInfo';

const StandardDetail: React.FC = () => {
  const { id: accreditationId, versionId, standardId } = useParams();
  const { getStandardType, isLoading: standardsLoading, version } = useStandardsContext();
  const { currentOrganizationId } = useContext(AppContext);

  const versionsInfo = useEnabledVersionsInfo(currentOrganizationId || '');

  const backUrl = useMemo(() => {
    if (versionsInfo.isLoading) {
      return `/admin/accreditation/${accreditationId}/versions/`;
    }

    const { hasSingleBody, hasSingleEnabledVersion } = versionsInfo;

    if (hasSingleBody && hasSingleEnabledVersion) {
      return '/admin/accreditation';
    }

    if (hasSingleBody) {
      return `/admin/accreditation/${accreditationId}/versions/`;
    }

    return '/admin/accreditation';
  }, [versionsInfo, accreditationId]);

  const standardByIdQuery = useAccreditationStandardById(
    currentOrganizationId,
    accreditationId!,
    versionId!,
    standardId || ''
  );

  const isLoading = standardByIdQuery.isLoading || standardByIdQuery.isFetching || standardsLoading;

  const [standardType, setStandardType] = useState<'standard' | 'subStandard' | 'requirement' | undefined>(undefined);
  const [standardTypeText, setStandardTypeText] = useState<string>('');

  const exportFullPoliciesMutation = usePortfolioExportFullPolicies(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
      console.log(t('awb.export.success'));
    },
    (error: any) => {
      // Error callback - could show an error toast here
      errorHelper.enqueueErrors(error as ServerErrorResponse);
      console.error(t('awb.export.error'), error);
    }
  );

  const exportTaggedDirectivesMutation = usePortfolioExportTaggedDirectives(
    () => {
      enqueueSnackbar(t('awb.export.success'), { variant: 'success' });
      console.log(t('awb.export.success'));
    },
    (error: any) => {
      // Error callback - could show an error toast here
      errorHelper.enqueueErrors(error as ServerErrorResponse);
      console.error(t('awb.export.error'), error);
    }
  );

  const handleExportFullPolicies = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      console.error('Missing required parameters for export');
      return;
    }

    exportFullPoliciesMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const handleExportTaggedDirectives = () => {
    if (!currentOrganizationId || !accreditationId || !versionId) {
      console.error('Missing required parameters for export');
      return;
    }

    exportTaggedDirectivesMutation.mutate({
      orgId: currentOrganizationId,
      accreditationBodyId: accreditationId,
      accreditationBodyVersionId: versionId,
    });
  };

  const setExportMenuItems = () => {
    const actions: ActionMenuItemProps[] = [];

    actions.push({
      key: 'exportAssessmentPortfolioFull',
      label: t('awb.export.assessmentPortfolioFull'),
      onClick: handleExportFullPolicies,
      disabled: exportFullPoliciesMutation.isLoading,
    });

    actions.push({
      key: 'exportAssessmentPortfolioTagged',
      label: t('awb.export.assessmentPortfolioTagged'),
      onClick: handleExportTaggedDirectives,
      disabled: exportTaggedDirectivesMutation.isLoading,
    });

    return actions;
  };

  useEffect(() => {
    if (!standardId || !version || standardsLoading) {
      setStandardType('standard');
      setStandardTypeText(t('awb.standards.new.standard'));
      return;
    }

    const type = getStandardType(standardId);
    setStandardType(type);

    switch (type) {
      case 'standard':
        setStandardTypeText(t('awb.standards.new.standard'));
        break;
      case 'subStandard':
        setStandardTypeText(t('awb.standards.new.subStandard'));
        break;
      case 'requirement':
        setStandardTypeText(t('awb.standards.new.requirement'));
        break;
      default:
        setStandardTypeText(t('awb.standards.new.standard'));
    }
  }, [standardId, getStandardType, version, standardsLoading, t]);

  const renderContent = () => {
    if (!standardId) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            itemsAlign: 'center',
            textAlign: 'center',
            minHeight: '60vh',
          }}
        >
          <img
            src={`${noStandardSelected}`}
            alt={'Select a standard image.'}
            style={{ height: '240px' }}
          />
          <Typography sx={{ color: neutral[500] }}>{t('awb.accreditationStandards.emptyScreen')}</Typography>
        </Box>
      );
    }

    return (
      <EditorSheet>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box>
            <Stack spacing={3}>
              <Typography variant='eyebrowMd'>{standardTypeText}</Typography>
              <Typography variant='subtitleMd'>{`${standardByIdQuery.data?.heading} ${standardByIdQuery.data?.title}`}</Typography>
              <Box sx={{ py: 3, minHeight: 0, overflow: 'auto' }}>
                {parse(standardByIdQuery.data?.draftContent ?? '', {
                  replace(node: DOMNode, ind: number) {
                    return PolicyContentHelper.handleContent(node, ind);
                  },
                })}
              </Box>
            </Stack>
          </Box>
        )}
      </EditorSheet>
    );
  };

  return (
    <TakeoverLayout
      pageTitle={version?.version || ''}
      backUrl={backUrl}
      leftDrawer={<StandardsList />}
      contentBackground='neutral'
      rightDrawer={
        standardId &&
        version && (
          <WrittenDirectivesSummary
            standardId={standardId}
            jurisdictions={
              Array.isArray(version?.jurisdictions) && typeof version?.jurisdictions[0] === 'object'
                ? (version.jurisdictions as Jurisdiction[])
                : []
            }
          />
        )
      }
      menubarLeft={
        <ActionMenu
          id='export_actions'
          btnText={t('awb.export.generateReport')}
          items={setExportMenuItems()}
          btnProps={{
            size: 'small',
            variant: 'outlined',
            color: 'neutral',
            disabled: exportFullPoliciesMutation.isLoading || exportTaggedDirectivesMutation.isLoading,
            loading: exportFullPoliciesMutation.isLoading || exportTaggedDirectivesMutation.isLoading,
          }}
        />
      }
    >
      {renderContent()}
    </TakeoverLayout>
  );
};

export default StandardDetail;
